// PLP content spot: fill the slot Boost renders as the first grid cell, and keep it filled
// across Boost re-renders (filter / sort / paginate).
//
// Previously this INSERTED the tile as a new grid child after Boost had already painted, which
// pushed every product down a slot — a ~0.33 layout shift on the collection page. Boost's
// product-item template now emits an empty `.wd-plp-spot-slot` cell on every render, and the
// section adds `html.wd-has-plp-spot` at parse time so CSS gives that cell its size. The grid is
// therefore laid out with the gap already present; this script only moves content into it, which
// costs no layout at all. Grid balancing is CSS now too, for the same reason.
(() => {
  const GRID = '.boost-sd__product-list';
  const SLOT = '.wd-plp-spot-slot';

  const source = document.querySelector('[data-plp-content-spot]');
  if (!source) return;
  const tile = source.querySelector('.plp-content-spot');
  if (!tile) return;

  // A position-0 edit spot owns the slot, and says so via this flag at init (drool-edit-spots.js
  // is a classic script, this is a deferred module, so the flag is always set by the time we read
  // it). Both scripts used to claim the slot on whichever Boost re-render they saw first, which
  // erased the other's content -- the edit spot vanished on filter change. Ownership is now
  // decided once, not per render.
  const editOwnsSlot = window.droolEditSpotOwnsSlot === true;

  // The tile starts inside a display:none source node, so a lazy image never becomes viewport-
  // eligible and fails to load once we relocate the tile. Force it to load eagerly (it's the
  // first, above-the-fold card) so the merchant's uploaded image always shows.
  const spotImg = editOwnsSlot ? null : tile.querySelector('.plp-content-spot__img');
  if (spotImg) {
    spotImg.loading = 'eager';
    if (!spotImg.complete) {
      const s = spotImg.getAttribute('src');
      if (s) spotImg.src = s;
    }
  }

  /* Collapse any slot we are not going to fill. Boost renders the slot at product index 0 of
     EVERY render, so page 2, page 3 and each "load more" batch get one too — and with a single
     tile those extras would sit there as empty cells. Hiding them is done here rather than with
     `.wd-plp-spot-slot:empty` in CSS, because on first paint the real slot is legitimately empty
     (the tile is moved in a moment later) and a CSS rule would collapse it and bring back the
     layout shift we removed. */
  const collapseSpare = () => {
    const slots = document.querySelectorAll(SLOT);
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      // The first slot is the one an edit spot fills, so keep it sized even while empty --
      // collapsing it would hide the spot that is about to be placed into it.
      if (slot.childElementCount || (editOwnsSlot && i === 0)) {
        slot.style.display = '';
      } else if (tile.parentElement && tile.parentElement !== slot) {
        // The tile lives in another slot already, so this one will never be filled.
        slot.style.display = 'none';
      }
    }
  };

  const fill = () => {
    const slots = document.querySelectorAll(SLOT);
    if (!slots.length) return false;

    // Prefer a slot that already holds the tile; otherwise take the first empty one. Skipped
    // entirely when an edit spot owns the slot -- we still run collapseSpare() below, because
    // the spare-slot cells on later pages are ours to tidy either way.
    if (!editOwnsSlot) {
      let target = null;
      for (let i = 0; i < slots.length; i++) {
        if (slots[i] === tile.parentElement) { target = slots[i]; break; }
        // Something else may have claimed a slot; skip anything already filled.
        if (!target && !slots[i].childElementCount) target = slots[i];
      }

      if (target && target !== tile.parentElement) {
        // appendChild moves the tile even when its previous slot was discarded by a re-render.
        target.appendChild(tile);
        // Boost marks the empty cell aria-hidden. Now that it holds a link, leaving that on
        // would hide a tab-reachable control from screen readers.
        target.removeAttribute('aria-hidden');
      }
    }

    collapseSpare();
    return true;
  };

  /* Watch the DOCUMENT, not the grid.
     Boost replaces the product-list node itself on filter / sort / paginate, so an observer bound
     to that node ends up watching a detached element and never fires again — which left the newly
     rendered slot empty after filtering. Observing document.body survives the swap.
     fill() runs synchronously inside the callback, before the browser paints, so the tile is never
     missing from a rendered frame. It is idempotent: once the tile is in the slot the next
     callback returns immediately, so our own insertion cannot loop. */
  let watching = false;
  const watch = () => {
    if (watching) return;
    watching = true;

    new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        if (!m.addedNodes.length && !m.removedNodes.length) continue;
        // Skip mutations we caused inside the slot itself.
        if (m.target && m.target.closest && m.target.closest(SLOT)) continue;
        fill();
        return;
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  // Arm the observer first: the grid is app-rendered and usually arrives after this script runs,
  // so the observer is what catches it. The poll is a backstop for the initial render only.
  watch();

  if (!fill()) {
    let tries = 0;
    const timer = setInterval(() => {
      if (fill() || ++tries > 40) clearInterval(timer);
    }, 250);
  }
})();
